import { useIsPresent, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

import { Box } from '@bonusly/design-system';

import type { BizyState } from '../../bizy';
import { deckCardLayer, deckRestPose, type DeckDirection, type DeckPose } from './bizy-deck-geometry';

// Animation layer for the person view's moment deck (BIZY-665/667), paired with
// the pure geometry in bizy-deck-geometry.ts: the timing, variants, and presence
// wrapper that carry a card as it is promoted out of the deck or settles back in.

// Enough travel to read as "off the deck" without theatrics.
export const DECK_SLIDE_X = 36;
export const DECK_TILT = 4;
// Ease-out so cards decelerate into place like something with weight; no bounce.
export const DECK_TRANSITION = { duration: 0.42, ease: [0.22, 1, 0.36, 1] } as const;
export const DECK_INSTANT = { duration: 0 } as const;
export const BIZY_DECK_GESTURE_TRANSITION = { duration: 0.24, ease: [0.22, 1, 0.36, 1] } as const;
export const BIZY_DECK_GESTURE_DURATION_MS = 560;
/** Stands in for the card's key until a deep link resolves to a real moment. */
export const DECK_PLACEHOLDER_KEY = '__bizy-deck-placeholder__';

export type DeckCustom = {
	direction: DeckDirection;
	/** Pose of the incoming card at the depth where it sat before this move. */
	pose: DeckPose;
	/** Pose where the outgoing card lands in the newly exposed deck. */
	exitPose: DeckPose;
	isCompact: boolean;
};

export type DeckAnimationTransition = typeof DECK_TRANSITION | typeof DECK_INSTANT;
export type DeckAnimationMeta = DeckCustom & { transition: DeckAnimationTransition };
export type DeckTransitionRegistry = Record<string, DeckAnimationMeta>;
export type SwipeStart = { pointerId: number; x: number; y: number };

/**
 * The outgoing card stays mounted while it animates away, so take it out of
 * play — otherwise its Prev/Next/Let's draft are briefly duplicated on top of
 * the card that's arriving.
 *
 * `inert` is what makes the `aria-hidden` beside it legitimate: the exiting card
 * can still hold focus for the whole exit, and aria-hiding a subtree that
 * contains focus is a WCAG 4.1.2 violation that Chrome refuses to apply. `inert`
 * blurs it and drops its buttons out of the tab order (which `pointerEvents:
 * none` does not), leaving nothing focused for `aria-hidden` to swallow.
 * `aria-hidden` still earns its place: it is what removes the duplicate controls
 * from the accessibility tree wherever `inert` is unsupported.
 */
export function DeckCardPresence({ children }: { children: ReactNode }) {
	const isPresent = useIsPresent();
	// React 18 has no typed `inert` prop, so it goes through as an attribute and
	// Chakra forwards it. React 19 makes `inert` a real boolean prop and warns on
	// the empty string, so this becomes `inert={!isPresent}` on that upgrade.
	const inertProps = isPresent ? {} : ({ inert: '' } as Record<string, string>);

	return (
		<Box aria-hidden={!isPresent || undefined} pointerEvents={isPresent ? undefined : 'none'} {...inertProps}>
			{children}
		</Box>
	);
}

/** Off the side of the deck: where a card leaves to, and comes back from. */
export const DECK_SLIDE_OFF = { opacity: 0, scale: 0.96, x: DECK_SLIDE_X, y: 10, rotate: DECK_TILT };

/**
 * Forward: the front card slides off to the right and the next one rises out of
 * the deck. Back: the front card settles into the deck and the previous card
 * slides in from the right, where it left.
 *
 * Both deck-side poses come from `deckRestPose`, so a card promoted out of the
 * deck starts on its ghost, and one settling back lands on the ghost that takes
 * its place. That shared pose is what makes the hand-off read as one card
 * moving rather than a card fading out while a different shape fades in.
 */
export function getDeckCardVariants(cardKey: string, incoming: DeckAnimationMeta): Variants {
	return {
		enter:
			incoming.direction === 1
				? {
						opacity: 0.4,
						...deckRestPose(incoming.pose, incoming.isCompact),
						transition: incoming.transition,
					}
				: { ...DECK_SLIDE_OFF, transition: incoming.transition },
		center: {
			opacity: 1,
			scale: 1,
			x: 0,
			y: 0,
			rotate: 0,
			zIndex: deckCardLayer(incoming.direction, 'incoming'),
			transition: incoming.transition,
		},
		exit: (registry: DeckTransitionRegistry) => {
			// AnimatePresence passes the latest registry to every exiting card.
			// Looking up by the card's captured key prevents a newer reversal from
			// retargeting an older exit that is still in flight.
			const outgoing = registry[cardKey] ?? incoming;
			const target = outgoing.direction === 1 ? DECK_SLIDE_OFF : { opacity: 0, ...deckRestPose(outgoing.exitPose, outgoing.isCompact) };

			return {
				...target,
				zIndex: deckCardLayer(outgoing.direction, 'outgoing'),
				transition: outgoing.transition,
			};
		},
	};
}

export function getDeckBizyState(isCompact: boolean, isGestureActive: boolean): BizyState {
	if (isGestureActive) return isCompact ? 'pointingLeft' : 'pointingRight';
	return isCompact ? 'curious' : 'lookingRight';
}
